import React from 'react';
import { Loader2 } from 'lucide-react';

function QuestionDisplay({
  user,
  handleSignOut,
  currentQuestion,
  questionData,
  localSelectedAnswer,
  setLocalSelectedAnswer,
  submitAnswer,
  isAnswerCorrect,
  pointsEarned,
}) {
  const pollActive = questionData.pool === 'on';
  const questionType = questionData.question_type;
  const correctAnswer = questionData.correct_answer;
  const optionCount = questionData.options_count ?? 5;
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isImageReady, setIsImageReady] = React.useState(questionData.question_photo === 'InClass');
  const [pendingAnswer, setPendingAnswer] = React.useState(false);
  const previousQuestionRef = React.useRef(currentQuestion.questionNumber);

  React.useEffect(() => {
    if (previousQuestionRef.current !== currentQuestion.questionNumber) {
      previousQuestionRef.current = currentQuestion.questionNumber;
      setLocalSelectedAnswer(null);
    }
    setPendingAnswer(false);
    setIsImageReady(questionData.question_photo === 'InClass');
  }, [currentQuestion.questionNumber, questionData.question_photo, setLocalSelectedAnswer]);

  const submitWithFeedback = (answer) => {
    setLocalSelectedAnswer(answer);
    setPendingAnswer(true);
    Promise.resolve(submitAnswer(answer)).finally(() => {
      setPendingAnswer(false);
    });
  };

  // Helper to format array/string answers
  const formatAnswer = (answer) => {
    if (Array.isArray(answer)) {
      return answer.sort().join(', ');
    }
    return answer || 'Not answered';
  };

  // Common styles for answer buttons
  const baseAnswerBtnClasses = `
    px-3 py-2 rounded-md border mr-2 mb-2
    transition-all
  `;

  // generate letters A, B, C... up to optionCount
  const letters = Array.from({ length: optionCount }, (_, i) =>
    String.fromCharCode(65 + i)
  );

  // Render different input blocks depending on question type
  let answerInputHtml = null;

  // -------------------------
  // Multiple Choice (Single)
  // -------------------------
  if (questionType === 'multiple_choice') {
    answerInputHtml = (
      <div className="my-4">
        <p className="text-[var(--text-color)] mb-2">Choose your answer:</p>
        {letters.map((option) => {
          const isSelected =
            localSelectedAnswer && localSelectedAnswer.includes(option);

          return (
            <button
              key={option}
              disabled={!pollActive}
              style={{ userSelect: 'none' }}
              onClick={() => {
                submitWithFeedback([option]);
              }}
              className={`
                ${baseAnswerBtnClasses}
                border-[var(--border-color)]
                ${isSelected
                  ? 'bg-[var(--primary-color)] text-white'
                  : 'bg-[var(--input-bg)] text-[var(--text-color)]'
                }
                ${pollActive ? 'hover:bg-[var(--hover-color)] hover:text-white' : 'cursor-not-allowed opacity-60'}
              `}
            >
              {option}
            </button>
          );
        })}
      </div>
    );
  }

  // -------------------------
  // Multiple Multiple Choice
  // -------------------------
  else if (questionType === 'multiple_multiple_choice') {
    answerInputHtml = (
      <div className="my-4">
        <p className="text-[var(--text-color)] mb-1 font-semibold">Multiple Answers</p>
        <p className="text-[var(--text-color)] mb-2">Choose your answers:</p>
        {letters.map((option) => {
          const isSelected =
            localSelectedAnswer && localSelectedAnswer.includes(option);

          return (
            <button
              key={option}
              disabled={!pollActive}
              style={{ userSelect: 'none' }}
              onClick={() => {
                let updatedAnswers = localSelectedAnswer
                  ? [...localSelectedAnswer]
                  : [];
                if (isSelected) {
                  updatedAnswers = updatedAnswers.filter((opt) => opt !== option);
                } else {
                  updatedAnswers.push(option);
                }
                submitWithFeedback(updatedAnswers);
              }}
              className={`
                ${baseAnswerBtnClasses}
                border-[var(--border-color)]
                ${isSelected
                  ? 'bg-[var(--primary-color)] text-white'
                  : 'bg-[var(--input-bg)] text-[var(--text-color)]'
                }
                ${!pollActive && 'cursor-not-allowed opacity-60'}
              `}
            >
              {option}
            </button>
          );
        })}
      </div>
    );
  }

  // -------------------------
  // Yes / No
  // -------------------------
  else if (questionType === 'yes_no') {
    answerInputHtml = (
      <div className="my-4">
        <p className="text-[var(--text-color)] mb-2">Choose your answer:</p>
        {['Yes', 'No'].map((option) => {
          const isSelected = localSelectedAnswer === option;
          return (
            <button
              key={option}
              disabled={!pollActive}
              style={{ userSelect: 'none' }}
              onClick={() => {
                submitWithFeedback(option);
              }}
              className={`
                ${baseAnswerBtnClasses}
                border-[var(--border-color)]
                ${isSelected
                  ? 'bg-[var(--primary-color)] text-white'
                  : 'bg-[var(--input-bg)] text-[var(--text-color)]'
                }
                ${pollActive ? 'hover:bg-[var(--hover-color)]' : 'cursor-not-allowed opacity-60'}
              `}
            >
              {option}
            </button>
          );
        })}
      </div>
    );
  }

  // -------------------------
  // Short Input
  // -------------------------
  else if (questionType === 'short_input') {
    const isInputTooLong =
      localSelectedAnswer && localSelectedAnswer.length > 20;

    answerInputHtml = (
      <div className="my-4">
        <p className="text-[var(--text-color)] mb-2">
          Type your answer (Max 20 characters):
        </p>
        <input
          type="text"
          id="short-answer"
          value={localSelectedAnswer || ''}
          placeholder="Enter your answer"
          onChange={(e) => setLocalSelectedAnswer(e.target.value)}
          disabled={!pollActive}
          maxLength={20}
          className={`
            w-full mb-2 px-3 py-2 border rounded-md transition-all
            border-[var(--border-color)] bg-[var(--input-bg)] text-[var(--text-color)]
            ${!pollActive ? 'opacity-60 cursor-not-allowed' : ''}
          `}
        />
        <button
          onClick={() => submitWithFeedback(localSelectedAnswer)}
          disabled={!pollActive || isInputTooLong || !localSelectedAnswer}
          style={{ userSelect: 'none' }}
          className={`
            ${baseAnswerBtnClasses}
            ${localSelectedAnswer && !isInputTooLong
              ? 'bg-[var(--primary-color)] text-white'
              : 'bg-[var(--input-bg)] text-[var(--text-color)]'
            }
            border-[var(--border-color)]
            ${pollActive ? 'hover:bg-[var(--hover-color]' : 'opacity-60 cursor-not-allowed'}
          `}
        >
          Submit
        </button>
        {isInputTooLong && (
          <p className="text-red-500 text-sm mt-2">
            Answer cannot exceed 20 characters.
          </p>
        )}
      </div>
    );
  }

  return (
    <section
      key={currentQuestion.questionNumber}
      className="
        w-full 
        sm:max-w-2xl md:max-w-3xl lg:max-w-4xl xl:max-w-5xl 
        mx-auto my-6 p-4 
        bg-[var(--card-bg)] text-[var(--text-color)] 
        rounded-lg shadow-md
      "
    >
      {/* Session Details */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-xl font-semibold">
            Question {currentQuestion.questionNumber}
          </h3>
          {pendingAnswer && (
            <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold" style={{ borderColor: 'var(--border-color)', color: 'var(--muted-color)' }}>
              <Loader2 size={13} className="animate-spin" aria-hidden="true" />
              Saving
            </span>
          )}
        </div>

        {/* If "InClass", show text instead of an image */}
        {questionData.question_photo === 'InClass' ? (
          <h2 className="text-center text-white font-medium p-2 rounded-md bg-[var(--primary-color)]">
            This question has been posted in class.
          </h2>
        ) : (
          <>
            {!isImageReady && (
              <div className="ce-question-loader rounded-lg border" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--alt-bg)' }}>
                <div className="ce-live-loader" aria-hidden="true">
                  <span>C</span>
                </div>
              </div>
            )}
            <img
              src={questionData.question_photo}
              alt="Question"
              onLoad={() => setIsImageReady(true)}
              onError={() => setIsImageReady(true)}
              onClick={() => setIsExpanded(true)}
              className={`cursor-zoom-in w-full h-auto rounded-md transition-all duration-300 ${isImageReady ? 'opacity-100' : 'opacity-0'}`}
              style={{ maxWidth: '100%', display: isImageReady ? 'block' : 'none' }}
            />
            {isExpanded && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
                <div className="relative">
                  <img
                    src={questionData.question_photo}
                    alt="Question Expanded"
                    className="max-w-full max-h-full rounded-md"
                  />
                  <button
                    onClick={() => setIsExpanded(false)}
                    className="absolute top-2 right-2 text-white text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Render the appropriate answer input */}
        {answerInputHtml}

        {/* Display correct/your answers if the correct answer is known */}
        {correctAnswer !== undefined && (
          <div className="mt-4">
            <h4 className="text-lg font-semibold text-green-500">
              Correct Answer: {formatAnswer(correctAnswer)}
            </h4>
            <h4
              className={`
                text-lg font-semibold 
                ${isAnswerCorrect === null
                  ? 'text-[var(--text-color)]'
                  : isAnswerCorrect
                    ? 'text-green-500'
                    : 'text-red-500'
                }
              `}
            >
              Your Answer: {formatAnswer(localSelectedAnswer)}
            </h4>
            <p className="text-sm text-[var(--muted-color)] mt-2">
              Points Earned: {pointsEarned}
            </p>
          </div>
        )}

        {/* Poll closed message */}
        {!pollActive && (
          <p className="text-sm text-[var(--muted-color)] italic">
            Poll is closed. You cannot change your answer.
          </p>
        )}
      </div>
    </section>
  );
}

export default QuestionDisplay;
